import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  DOWNLOAD_SIZE,
  archiveQrCode,
  deleteQrCode,
  downloadQrCodeFile,
  getQrCode,
  getQrCodeImage,
  getQrCodeScans,
  getQrCodeStats,
  replaceQrCodeFile,
  restoreQrCode,
  updateQrCode,
} from '../api/qrCodes';
import { listGroups } from '../api/groups';
import { saveBlob } from '../download';
import { formatTimestamp } from '../format';
import type { Group, QrCode, QrCodeFile, QrCodeStats, QrImageFormat, ScanEvent } from '../api/types';
import { CopyButton } from '../components/CopyButton';
import { useDialog } from '../components/Dialog';
import { ErrorBanner } from '../components/ErrorBanner';
import { FieldLabel } from '../components/FieldLabel';
import { FileDropzone } from '../components/FileDropzone';
import { FileVersions } from '../components/FileVersions';
import { DEFAULT_IMAGE_STYLE, type ImageStyle } from '../components/ImageStyleFields';
import { QrArtwork } from '../components/QrArtwork';
import { isHttpUrl, type QrCodeFormValues } from '../components/QrCodeForm';
import {
  LanguageTargetsEditor,
  MultiLanguageSwitch,
  defaultTargetUrl,
  languageTargetsError,
  normalizedLanguageTargets,
  startLanguages,
} from '../components/LanguageTargetsEditor';
import { productCodeTitle, productPageUrl } from '../components/ProductCodeForm';
import { ProductPicker } from '../components/ProductPicker';
import { useCurrentUser } from '../auth/CurrentUserProvider';
import { StatusPill, statusOf } from '../components/StatusPill';
import { useToast } from '../components/Toast';
import { StatsPanel } from '../components/StatsPanel';
import { ContactCardPanel } from '../components/ContactCardPanel';
import { ownerGroupLabel } from '../ownerGroup';
import { TypeTag } from '../components/TypeTag';

const SCAN_REFRESH_MS = 5000;

export function QrDetailPage() {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const dialog = useDialog();
  const toast = useToast();
  const { id = '' } = useParams();
  const navigate = useNavigate();

  const [qrCode, setQrCode] = useState<QrCode | null>(null);
  const [stats, setStats] = useState<QrCodeStats | null>(null);
  const [scans, setScans] = useState<ScanEvent[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [style, setStyle] = useState<ImageStyle>(DEFAULT_IMAGE_STYLE);
  // Null is view mode: one piece of state rather than a flag beside the values, so the two cannot disagree.
  const [draft, setDraft] = useState<QrCodeFormValues | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const editing = draft !== null;

  /*
   * The groups the reader may create codes in, plus the one it is in already - kept even when they are not a
   * member of it, because a shared code is readable by people who cannot create in it.
   */
  const movableTo = useMemo(() => {
    if (!qrCode) {
      return [];
    }

    const editable = groups
      .filter((group) => group.canEditQrCodes)
      .map((group) => ({ id: group.id, name: group.name }));

    return editable.some((group) => group.id === qrCode.ownerGroupId)
      ? editable
      : [{ id: qrCode.ownerGroupId, name: ownerGroupLabel(qrCode, t, user?.personalSpaceId) }, ...editable];
  }, [groups, qrCode, t, user?.personalSpaceId]);

  // Only needed to offer the move, so a failure to read them costs the group select and nothing else.
  useEffect(() => {
    listGroups()
      .then(setGroups)
      .catch(() => setGroups([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [loadedCode, loadedStats, loadedScans] = await Promise.all([
        getQrCode(id),
        getQrCodeStats(id),
        getQrCodeScans(id),
      ]);
      setQrCode(loadedCode);
      setStats(loadedStats);
      setScans(loadedScans);
      setError(null);
    } catch (caught) {
      setError(caught);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const scanning = qrCode?.kind === 'File' && qrCode.files[0]?.status === 'Scanning';
  useEffect(() => {
    if (!scanning) {
      return;
    }

    const timer = window.setTimeout(() => {
      getQrCode(id)
        .then(setQrCode)
        .catch(() => undefined);
    }, SCAN_REFRESH_MS);
    return () => window.clearTimeout(timer);
  }, [id, scanning, qrCode]);

  // The image endpoint needs the bearer token, so it is fetched as a blob rather than used as an <img src>.
  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    getQrCodeImage(id, 'png', 10, style.foreground, style.background, style.logoStyle)
      .then((blob) => {
        if (cancelled) {
          return;
        }

        objectUrl = URL.createObjectURL(blob);
        setImageUrl(objectUrl);
      })
      .catch(setError);

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [id, style]);

  function startEditing() {
    if (!qrCode) {
      return;
    }

    setValidationError(null);
    setDraft({
      ownerGroupId: qrCode.ownerGroupId,
      title: qrCode.title,
      kind: qrCode.kind,
      targetUrl: qrCode.targetUrl,
      languageTargets: qrCode.languageTargets,
      product:
        qrCode.kind === 'Product' && qrCode.productRouCode
          ? {
              rouCode: qrCode.productRouCode,
              countryCode: qrCode.productCountryCode ?? '',
              productName: qrCode.productName ?? qrCode.productRouCode,
              kiteProductNumbers: qrCode.productKiteNumbers,
            }
          : undefined,
      isActive: qrCode.isActive,
    });
  }

  function cancelEditing() {
    setDraft(null);
    setValidationError(null);
  }

  async function handleUpdate() {
    if (!draft) {
      return;
    }

    const title = draft.title.trim();
    const isProduct = draft.kind === 'Product';
    const isFile = draft.kind === 'File';
    const languages = isProduct || isFile ? [] : (draft.languageTargets ?? []);
    const multiLanguage = languages.length > 0;
    const targetUrl = multiLanguage ? defaultTargetUrl(languages).trim() : (draft.targetUrl?.trim() ?? '');

    // The same rules the create form applies, so a code cannot be edited into a state it could not be created in.
    if (!title) {
      setValidationError(t('qrCode.validation.titleRequired'));
      return;
    }

    if (isFile) {
      // Nothing to check: the file, when one is chosen, was checked as it was picked.
    } else if (isProduct) {
      if (!draft.product) {
        setValidationError(t('qrCode.validation.productRequired'));
        return;
      }
    } else if (multiLanguage) {
      const languagesError = languageTargetsError(languages, t);
      if (languagesError) {
        setValidationError(languagesError);
        return;
      }
    } else if (!isHttpUrl(targetUrl)) {
      setValidationError(t('qrCode.validation.urlRequired'));
      return;
    }

    setValidationError(null);
    setSubmitting(true);
    try {
      if (isFile && draft.file) {
        await replaceQrCodeFile(id, draft.file);
      }

      const updated = await updateQrCode(id, {
        title,
        kind: draft.kind,
        targetUrl: isProduct || isFile ? undefined : targetUrl,
        languageTargets: isProduct || isFile ? undefined : normalizedLanguageTargets(languages),
        product: isProduct ? draft.product : undefined,
        isActive: draft.isActive,
        ownerGroupId: draft.ownerGroupId,
      });
      setQrCode(updated);
      setDraft(null);
      setError(null);
      toast.success(t('toast.updated'));
    } catch (caught) {
      setError(caught);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleArchive() {
    if (!qrCode) {
      return;
    }

    const confirmed = await dialog.confirm({
      title: t('qrCode.archiveTitle'),
      body: t('qrCode.archiveBody', { title: qrCode.title }),
      confirmLabel: t('qrCode.archive'),
      tone: 'primary',
      icon: 'pi-inbox',
    });

    if (!confirmed) {
      return;
    }

    try {
      setQrCode(await archiveQrCode(id));
      setDraft(null);
      setError(null);
      toast.success(t('toast.archived', { title: qrCode.title }));
    } catch (caught) {
      setError(caught);
    }
  }

  // Nothing is lost by restoring, so nothing is asked before it.
  async function handleRestore() {
    try {
      const restored = await restoreQrCode(id);
      setQrCode(restored);
      setError(null);
      toast.success(t('toast.restored', { title: restored.title }));
    } catch (caught) {
      setError(caught);
    }
  }

  // Super admins only: the identifier goes back into circulation, and a later code could inherit it.
  async function handleDelete() {
    if (!qrCode) {
      return;
    }

    const confirmed = await dialog.confirm({
      title: t('qrCode.deleteTitle'),
      body: t('qrCode.deleteBody', { title: qrCode.title, slug: qrCode.slug }),
      confirmLabel: t('qrCode.deleteConfirm'),
      icon: 'pi-exclamation-triangle',
    });

    if (!confirmed) {
      return;
    }

    try {
      await deleteQrCode(id);
      // Said after the page has gone: the toast outlives the route, which is the only way anybody sees it.
      toast.success(t('toast.deleted', { title: qrCode.title }));
      navigate('/');
    } catch (caught) {
      setError(caught);
    }
  }

  async function downloadFile(file: QrCodeFile) {
    try {
      saveBlob(await downloadQrCodeFile(id, file.id), file.fileName);
    } catch (caught) {
      setError(caught);
    }
  }

  async function download(format: QrImageFormat) {
    try {
      const blob = await getQrCodeImage(
        id,
        format,
        DOWNLOAD_SIZE[format],
        style.foreground,
        style.background,
        style.logoStyle,
      );
      saveBlob(blob, `qr-${qrCode?.slug ?? id}.${format}`);
    } catch (caught) {
      await dialog.alert({
        title: t('common.notAvailable'),
        body: caught instanceof Error ? caught.message : String(caught),
        tone: 'danger',
      });
    }
  }

  if (loading && !qrCode) {
    return (
      <p>
        <i className="pi pi-spin pi-spinner me-2" aria-hidden="true" />
        {t('app.loading')}
      </p>
    );
  }

  if (!qrCode) {
    return (
      <>
        <ErrorBanner error={error} />
        <p>
          <Link to="/">
            <i className="pi pi-arrow-left me-1" aria-hidden="true" />
            {t('qrCode.backToList')}
          </Link>
        </p>
      </>
    );
  }

  return (
    <>
      <p>
        <Link to="/" className="btn btn-link ps-0">
          <i className="pi pi-arrow-left me-1" aria-hidden="true" />
          {t('qrCode.backToList')}
        </Link>
      </p>

      <ErrorBanner error={error} />

      {qrCode.isArchived && (
        <div className="alert alert-secondary d-flex gap-2" role="status">
          <i className="pi pi-inbox mt-1" aria-hidden="true" />
          <span>{t('qrCode.archivedNotice')}</span>
        </div>
      )}

      <div className="link-card">
        <div className="link-card__header">
          <div className="link-card__identity">
            {/* The code's name, where the design put its identifier. The identifier is something you copy,
                not something you read, so it sits in the metadata list below. */}
            {/* Follows the field while it is being edited, so the heading and the input never disagree. */}
            <h2 className="link-card__title" title={qrCode.title}>
              <i className="pi pi-qrcode" aria-hidden="true" />
              <span>{draft?.title.trim() ? draft.title : qrCode.title}</span>
            </h2>

            <StatusPill status={statusOf(qrCode)} />
            <TypeTag qrCode={qrCode} />

            {!qrCode.canEdit && (
              <span className="link-card__readonly" title={t('qrCode.readOnlyAccess')}>
                <i className="pi pi-lock" aria-hidden="true" />
                {t('qrCode.readOnly')}
              </span>
            )}
          </div>

          <div className="link-card__actions">
            <button type="button" className="lc-btn" onClick={() => void download('png')}>
              <i className="pi pi-image" aria-hidden="true" />
              {t('common.downloadPng')}
            </button>
            <button type="button" className="lc-btn" onClick={() => void download('svg')}>
              <i className="pi pi-file" aria-hidden="true" />
              {t('common.downloadSvg')}
            </button>
          </div>
        </div>

        <div className="link-card__divider" />

        <div className="link-card__body">
          <QrArtwork
            idPrefix="qr"
            imageUrl={imageUrl}
            alt={`QR code ${qrCode.title}`}
            loadingLabel={t('qrCode.rendering')}
            transparentHint={t('qrCode.backgroundTransparentHint')}
            style={style}
            onStyleChange={setStyle}
          />

          <div className="link-card__details">
            <dl className="link-meta">
              {/* Editing turns the record into fields in place. The rows that cannot change stay as they were:
                  the printed link and its identifier are on paper somewhere, and the group decides who may
                  touch the code at all. */}
              {qrCode.kind === 'Product' && (
                <>
                  <dt>
                    <i className="pi pi-box" aria-hidden="true" />
                    {editing ? (
                      <FieldLabel htmlFor="product-search" required>
                        {t('qrCode.product')}
                      </FieldLabel>
                    ) : (
                      t('qrCode.product')
                    )}
                  </dt>
                  <dd>
                    {/* Repointing a re-coded product here is what fixes every label already printed, so it
                        is the one thing a product code has that is worth editing. The country is part of
                        the code's identity and stays put. */}
                    {draft ? (
                      <ProductPicker
                        selection={draft.product ?? null}
                        countryCode={qrCode.productCountryCode}
                        onChange={(product) =>
                          setDraft({
                            ...draft,
                            product: product ?? undefined,
                            // The saved title has to follow too. Deriving it only for display sent the
                            // previous product's name to the API.
                            title: product ? productCodeTitle(product) : draft.title,
                          })
                        }
                        disabled={submitting}
                        showLabel={false}
                      />
                    ) : (
                      <span>
                        {qrCode.productName ?? qrCode.productRouCode}{' '}
                        <span className="mono text-body-secondary">
                          ({qrCode.productRouCode} &middot; {qrCode.productCountryCode})
                        </span>
                      </span>
                    )}
                  </dd>
                </>
              )}

              <dt>
                <i className="pi pi-tag" aria-hidden="true" />
                {editing ? (
                  <FieldLabel htmlFor="qr-title" required={draft?.kind !== 'Product'}>
                    {t('qrCode.title')}
                  </FieldLabel>
                ) : (
                  t('qrCode.title')
                )}
              </dt>
              <dd>
                {draft ? (
                  <input
                    id="qr-title"
                    type="text"
                    className="form-control form-control-sm"
                    aria-required={draft.kind !== 'Product'}
                    value={draft.title}
                    disabled={submitting || draft.kind === 'Product'}
                    onChange={(event) => setDraft({ ...draft, title: event.target.value })}
                  />
                ) : (
                  qrCode.title
                )}
              </dd>

              <dt>
                <i className="pi pi-link" aria-hidden="true" />
                {t('qrCode.printedUrl')}
              </dt>
              <dd className="link-meta__inline">
                <span className="mono text-truncate">{qrCode.redirectUrl}</span>
                {/* The padlock carries the meaning; hovering or reading it out gives the reason. */}
                <i
                  className="pi pi-lock"
                  role="img"
                  aria-label={t('qrCode.linkIdLockedHint')}
                  title={t('qrCode.linkIdLockedHint')}
                />
                <CopyButton value={qrCode.redirectUrl} />
              </dd>

              {qrCode.kind === 'File' ? (
                <>
                  <dt>
                    <i className="pi pi-file" aria-hidden="true" />
                    {t('qrCode.file.label')}
                  </dt>
                  <dd>
                    {draft ? (
                      <div className="file-edit">
                        <FileVersions qrCode={qrCode} onDownload={(file) => void downloadFile(file)} />
                        <div className="file-edit__replace">
                          <FileDropzone
                            id="qr-file"
                            label={t('qrCode.file.replace')}
                            file={draft.file ?? null}
                            disabled={submitting}
                            onChange={(file) => setDraft({ ...draft, file: file ?? undefined })}
                          />
                        </div>
                      </div>
                    ) : (
                      <FileVersions qrCode={qrCode} onDownload={(file) => void downloadFile(file)} />
                    )}
                  </dd>
                </>
              ) : (
                <>
                  <dt>
                    <i className="pi pi-external-link" aria-hidden="true" />
                    {editing ? (
                      <FieldLabel htmlFor="qr-target" required={draft?.kind !== 'Product'}>
                        {t('qrCode.destinationUrl')}
                      </FieldLabel>
                    ) : (
                      t('qrCode.currentDestination')
                    )}
                  </dt>
                  <dd>
                    {qrCode.contact ? (
                      <span className="link-meta__inline">
                        <a href={qrCode.targetUrl} target="_blank" rel="noreferrer noopener" className="mono text-truncate">
                          {qrCode.targetUrl}
                        </a>
                        <i
                          className="pi pi-lock"
                          role="img"
                          aria-label={t('qrCode.contact.targetLocked')}
                          title={t('qrCode.contact.targetLocked')}
                        />
                      </span>
                    ) : draft && draft.kind === 'Product' ? (
                      <input
                        id="qr-target"
                        type="text"
                        className="form-control form-control-sm"
                        value={draft.product
                          ? productPageUrl(user?.productPageBaseUrl ?? '', draft.product)
                          : qrCode.targetUrl}
                        disabled
                        readOnly
                      />
                    ) : draft ? (
                      <div className="destination-row">
                        <div className="destination-row__value">
                          {(draft.languageTargets ?? []).length > 0 ? (
                            <LanguageTargetsEditor
                              id="qr-target"
                              value={draft.languageTargets ?? []}
                              disabled={submitting}
                              onChange={(languageTargets) => setDraft({ ...draft, languageTargets })}
                            />
                          ) : (
                            <input
                              id="qr-target"
                              type="url"
                              className="form-control form-control-sm"
                              aria-required="true"
                              value={draft.targetUrl}
                              placeholder="https://example.com/landing-page"
                              disabled={submitting}
                              onChange={(event) => setDraft({ ...draft, targetUrl: event.target.value })}
                            />
                          )}
                        </div>
                        <MultiLanguageSwitch
                          checked={(draft.languageTargets ?? []).length > 0}
                          disabled={submitting}
                          onChange={(on) =>
                            setDraft(
                              on
                                ? { ...draft, languageTargets: startLanguages(draft.targetUrl?.trim() ?? '') }
                                : { ...draft, targetUrl: defaultTargetUrl(draft.languageTargets ?? []), languageTargets: [] },
                            )
                          }
                        />
                      </div>
                    ) : qrCode.languageTargets.length > 0 ? (
                      <ul className="language-links" aria-label={t('qrCode.languages.title')}>
                        {[...qrCode.languageTargets]
                          .sort((a, b) => Number(b.isDefault) - Number(a.isDefault))
                          .map((target) => (
                            <li key={target.language}>
                              <span className={`language-chip mono${target.isDefault ? ' is-default' : ''}`}>
                                {target.language}
                                {target.isDefault && (
                                  <span role="img" aria-label={t('qrCode.languages.default')} title={t('qrCode.languages.default')}>
                                    *
                                  </span>
                                )}
                              </span>
                              <a href={target.targetUrl} target="_blank" rel="noreferrer noopener" className="mono" title={target.targetUrl}>
                                {target.targetUrl}
                              </a>
                            </li>
                          ))}
                      </ul>
                    ) : (
                      <a href={qrCode.targetUrl} target="_blank" rel="noreferrer noopener" className="mono">
                        {qrCode.targetUrl}
                      </a>
                    )}
                  </dd>
                </>
              )}

              <dt>
                {/* The dot follows the state, and while editing it follows the switch beside it. */}
                <i
                  className={`pi pi-circle-fill status-dot ${
                    draft ? (draft.isActive ? 'active' : 'inactive') : statusOf(qrCode)
                  }`}
                  aria-hidden="true"
                />
                {editing ? (
                  <label htmlFor="qr-active">{t('qrCode.status')}</label>
                ) : (
                  t('qrCode.status')
                )}
              </dt>
              <dd>
                {draft ? (
                  <div className="form-check form-switch">
                    <input
                      id="qr-active"
                      type="checkbox"
                      role="switch"
                      className="form-check-input"
                      checked={draft.isActive}
                      disabled={submitting}
                      onChange={(event) => setDraft({ ...draft, isActive: event.target.checked })}
                    />
                    <label className="form-check-label" htmlFor="qr-active">
                      {t('qrCode.activeToggle')}
                    </label>
                  </div>
                ) : qrCode.isArchived ? (
                  t('qrCode.archived')
                ) : qrCode.isActive ? (
                  t('qrCode.active')
                ) : (
                  t('qrCode.inactive')
                )}
              </dd>

              <dt>
                <i className="pi pi-users" aria-hidden="true" />
                {editing && !qrCode.inPersonalSpace ? (
                  <label htmlFor="qr-group">{t('common.group')}</label>
                ) : (
                  t('common.group')
                )}
              </dt>
              <dd>
                {/* Moving a code hands it to a different set of people, so the list holds the groups the
                    reader may create codes in - exactly what the API will accept - plus the one it is in. */}
                {draft && !qrCode.inPersonalSpace ? (
                  <select
                    id="qr-group"
                    className="form-select form-select-sm"
                    value={draft.ownerGroupId}
                    disabled={submitting}
                    onChange={(event) => setDraft({ ...draft, ownerGroupId: event.target.value })}
                  >
                    {movableTo.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  ownerGroupLabel(qrCode, t, user?.personalSpaceId)
                )}
              </dd>

              <dt>
                <i className="pi pi-calendar-plus" aria-hidden="true" />
                {t('qrCode.created')}
              </dt>
              <dd>
                {t('qrCode.createdBy', {
                  date: formatTimestamp(qrCode.createdAt),
                  name: qrCode.createdByName ?? qrCode.createdBy,
                })}
              </dd>

              {/* Always a row, even on a code nobody has touched: a list whose rows come and go is one you
                  have to re-read every time. */}
              <dt>
                <i className="pi pi-pencil" aria-hidden="true" />
                {t('qrCode.lastModified')}
              </dt>
              <dd>
                {qrCode.updatedAt
                  ? t('qrCode.createdBy', {
                      date: formatTimestamp(qrCode.updatedAt),
                      name: qrCode.updatedByName ?? qrCode.updatedBy,
                    })
                  : t('qrCode.neverModified')}
              </dd>
            </dl>

            {validationError && (
              <div className="alert alert-danger d-flex gap-2 mb-0" role="alert">
                <i className="pi pi-exclamation-circle mt-1" aria-hidden="true" />
                <span>{validationError}</span>
              </div>
            )}

            {/* Always present now: even a reader who can change nothing has the history to go to, and it
                is the answer to the question a printed code raises. */}
            <div className="link-card__footer">
              {/* What the record can lose. Both are offered while editing only: neither is something to reach
                  for while reading, and one of them cannot be undone. */}
              <div className="link-card__footer-group">
                {editing && qrCode.canEdit && !qrCode.isArchived && (
                  <button type="button" className="lc-btn" onClick={() => void handleArchive()}>
                    <i className="pi pi-inbox" aria-hidden="true" />
                    {t('qrCode.archive')}
                  </button>
                )}
                {/* Super admins only, and only after the warning. Also offered outside edit mode to somebody
                    who has no edit mode to enter - a super admin who is not in the owning group - or deleting
                    would be unreachable for them. */}
                {qrCode.canDelete && (editing || !qrCode.canEdit) && (
                  <button
                    type="button"
                    className="lc-btn lc-btn--danger"
                    onClick={() => void handleDelete()}
                  >
                    <i className="pi pi-trash" aria-hidden="true" />
                    {t('common.delete')}
                  </button>
                )}
              </div>

              {/* What the record can become. */}
              <div className="link-card__footer-group">
                {draft && (
                  <>
                    <button
                      type="button"
                      className="lc-btn"
                      onClick={cancelEditing}
                      disabled={submitting}
                    >
                      <i className="pi pi-times" aria-hidden="true" />
                      {t('common.cancel')}
                    </button>
                    <button
                      type="button"
                      className="lc-btn lc-btn--primary"
                      onClick={() => void handleUpdate()}
                      disabled={submitting}
                    >
                      <i className={`pi ${submitting ? 'pi-spinner' : 'pi-check'}`} aria-hidden="true" />
                      {submitting ? t('common.saving') : t('common.save')}
                    </button>
                  </>
                )}
                {/* Offered while reading rather than while editing, and to everybody who can see the code:
                    the history is the answer to "who repointed this", which is asked by whoever is holding
                    the printed code, not by whoever is about to change it. */}
                {!editing && (
                  <Link to={`/qr/${qrCode.id}/audit`} className="lc-btn">
                    <i className="pi pi-history" aria-hidden="true" />
                    {t('qrCode.audit.open')}
                  </Link>
                )}
                {!editing && qrCode.canEdit && !qrCode.isArchived && (
                  <button type="button" className="lc-btn lc-btn--primary" onClick={startEditing}>
                    <i className="pi pi-pencil" aria-hidden="true" />
                    {t('common.edit')}
                  </button>
                )}
                {/* How an archived code comes back. It has no edit mode, so this is its one action. */}
                {qrCode.canEdit && qrCode.isArchived && (
                  <button type="button" className="lc-btn lc-btn--primary" onClick={() => void handleRestore()}>
                    <i className="pi pi-replay" aria-hidden="true" />
                    {t('qrCode.restore')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {qrCode.contact && <ContactCardPanel qrCode={qrCode} onSaved={setQrCode} />}

      <div className="card">
        <h2>
          <i className="pi pi-chart-bar me-2" aria-hidden="true" />
          {t('stats.title')}
        </h2>
        {/* Defensive: the code, its statistics and its scans arrive from one Promise.all, so in practice
            there is no window where the code is on screen without them. */}
        {stats ? (
          <StatsPanel stats={stats} scans={scans} />
        ) : (
          <p>
            <i className="pi pi-spin pi-spinner me-2" aria-hidden="true" />
            {t('app.loading')}
          </p>
        )}
      </div>
    </>
  );
}
